import cn from 'classnames';
import { mapTime, splitTitle } from 'components/Shared/Product/selector';
import { ProductTag, getProductTagKey } from './productTag';
import { StarRating } from 'components/Shared/StarRating';
import Image from 'components/Shared/Image';
import styles from './styles.scss';
import { NO_ELEMENT, rm } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import { BhImage } from 'models/BhImage';
import { NotifyLink } from 'components/Shared/NotifyLink';
import { SrcSet } from 'components/Shared/Image/SrcSet';
import ResponsiveImage from 'components/Shared/Image/ResponsiveImage';
import { IBasicProduct } from 'models/BasicProduct';
import { isEmpty } from 'utils/isEmpty';
import { trimEnd } from 'utils/trim';
import { Time } from 'models/ProductMeta';
import { ProductlistViewProduct } from 'models/ProductlistViewProduct';
import { useState, memo, forwardRef, Ref } from 'react';
import { useDebounce } from 'hooks';
import connect from 'components/Shared/connect';
import { isMobile } from 'components/Shared/responsive';
import { FavouriteToggle } from 'components/FavouriteToggle';
import { EnergyData } from 'components/EnergyData';
import { ProductProperties } from '../../usp/productProperties';
import {
  CardPrice,
  PriceProps as BetterPriceProps
} from 'components/Shared/Price/themes/BGH/cardPrice';
import { isMobile as isMobileSelector } from 'components/Shared/responsive';
import { useSelector } from 'react-redux';
import { TacticalFlag, TacticalFlagProps } from 'components/TacticalFlag';
import { createGtmProduct } from 'store/googleTagManager/events/utils';
import { GtmProduct } from 'store/googleTagManager/types';
interface ImageProps {
  isListView?: boolean;
  isLazy?: boolean;
  productId: string;
  ticket: string;
  url: string;
  image?: BhImage;
  picture: {
    className?: string;
    height: number;
    width: number;
  };
  className?: string;
  isSponsored?: boolean;
  customSizes?: { [key: string]: SrcSet };
  isDisabled?: boolean;
}

interface TitleClassNames {
  outerWrapper?: string;
  innerWrapper?: string;
}
type TitleProps = Pick<IBasicProduct, 'titleSplitPos' | 'title' | 'ticket' | 'url'> & {
  classNames?: TitleClassNames;
  isCategoryBold?: boolean;
};

const DEFAULT_WIDTH = 321;
const MEDIUM_WIDTH = 229;

const sizes: { [key: string]: SrcSet } = {
  default: {
    viewWidth: DEFAULT_WIDTH,
    width: DEFAULT_WIDTH,
    height: DEFAULT_WIDTH
  },
  medium: {
    viewWidth: MEDIUM_WIDTH,
    width: MEDIUM_WIDTH,
    height: MEDIUM_WIDTH
  }
};

interface SponsoredProps {
  isSponsored?: boolean;
}

const Sponsored = ({ isSponsored }: SponsoredProps) => {
  if (!isSponsored) {
    return NO_ELEMENT;
  }

  return (
    <div className={cn(styles.switchSponsoredContainer, styles.desktopSponsoredContainer)}>
      <p className={styles.switchSponsoredText}>Sponsrad</p>
    </div>
  );
};

const HeaderImage = ({
  image,
  url,
  picture,
  ticket,
  className,
  isSponsored,
  isListView,
  isLazy,
  productId,
  customSizes,
  isDisabled,
  gtmProduct
}: ImageProps & Pick<OwnProps, 'gtmProduct'>) => {
  const s = customSizes || sizes;
  const responsive: ResponsiveImage = {
    srcSet: Object.values(s),
    sizes: `(max-width: 768px) ${s.default.width}px,
    ${s.medium.width}px`
  };

  return (
    <div className={className}>
      <NotifyLink url={url} ticket={ticket} className={className} isDisabled={isDisabled}>
        <Image
          lazy={isLazy}
          wrapperStyle={{ width: '100%' }}
          className={cn(styles.image, picture.className, {
            [styles.imageWithoutBorder]: isListView
          })}
          paddingBottom
          {...image}
          responsive={responsive}
          height={picture.height}
          width={picture.width}
          alt={image?.alt}
        />
        <Sponsored isSponsored={isSponsored} />
      </NotifyLink>
      <FavouriteToggle className={styles.favourite} productId={productId} gtmProduct={gtmProduct} />
    </div>
  );
};

export const Title = (props: TitleProps) => {
  const { classNames = {}, url, ticket } = props;
  const { title, category } = splitTitle(props);
  return (
    <NotifyLink className={classNames.outerWrapper} url={url} ticket={ticket}>
      <div className={cn(classNames.innerWrapper, styles.title)}>
        <p>{category}</p>
        <p className={styles.bold}>{title}</p>
      </div>
    </NotifyLink>
  );
};

interface ReviewProps extends TacticalFlagProps {
  score: number;
  isTopTested?: boolean;
  isNewProduct: boolean;
  isListView?: boolean;
}

const Review = ({
  score,
  isTopTested,
  isNewProduct,
  tacticalFlagMarking,
  tacticalFlagColor,
  isListView
}: ReviewProps) => {
  const roundedScore = Math.floor(score);
  const _score = <StarRating score={score} />;
  const tagType = getProductTagKey({ isNewProduct, isTopTested });
  const _tag = <ProductTag type={tagType} />;
  const _flag = (
    <TacticalFlag tacticalFlagMarking={tacticalFlagMarking} tacticalFlagColor={tacticalFlagColor} />
  );
  const shouldRender = !!roundedScore || !!tagType || !!tacticalFlagMarking;

  return shouldRender ? (
    <div className={cn(styles.reviewContainer, { [styles.marginTop]: !isListView })}>
      {_score}
      {_tag}
      {_flag}
    </div>
  ) : (
    NO_ELEMENT
  );
};

interface DeliveryProps {
  isListView?: boolean;
  meta?: {
    deliveryEstimate?: number;
    showDeliveryInfo: boolean;
    stock: string;
  };
}

const showStock = (showDeliveryInfo: boolean, stock: string): boolean => {
  return !showDeliveryInfo && !isEmpty(stock);
};

const isShortDeliveryTime = (deliveryEstimate: number): boolean => {
  switch (deliveryEstimate) {
    case Time.TwoToThreeWorkDays:
    case Time.TwoToFiveWorkDays:
    case Time.ThreeToFiveWorkDays:
    case Time.FourToSevenWorkDays:
    case Time.FiveToTenWorkDays:
      return true;
    default:
      return false;
  }
};

const Delivery = ({ meta, isListView }: DeliveryProps) => {
  if (
    !meta ||
    !meta.deliveryEstimate ||
    isListView ||
    (isShortDeliveryTime(meta.deliveryEstimate) && !showStock(meta.showDeliveryInfo, meta.stock))
  ) {
    return NO_ELEMENT;
  }

  const delivery = showStock(meta.showDeliveryInfo, meta.stock)
    ? trimEnd(meta.stock, ':')
    : `${rm('filter_title_p_deltime')} ${mapTime(meta.deliveryEstimate)}`;
  return <p className={styles.deliveryText}>{delivery}</p>;
};

interface VariantOptionProps extends Pick<ProductlistViewProduct, 'colorOptions'> {
  options?: string[];
  setImage?: (image: BhImage) => void;
  defaultImage: BhImage;
  isMobile: boolean;
  hideVariants?: boolean;
  maxVariants?: number;
}

const _VariantOptions = memo(
  ({
    colorOptions,
    url,
    ticket,
    setImage,
    defaultImage,
    hideVariants,
    maxVariants = 0,
    isMobile
  }: VariantOptionProps & ImageProps) => {
    const VARIANT_DEBOUNCE_TIME = 150;
    const IMAGE_SIZE = 64;
    const [debounce] = useDebounce(
      (updateImage: () => void) => updateImage(),
      VARIANT_DEBOUNCE_TIME
    );
    if (!colorOptions?.nbrOfColors || hideVariants) {
      return NO_ELEMENT;
    }
    const { colors, nbrOfColors } = colorOptions;
    const hasTooManyOptions = nbrOfColors > maxVariants;
    const amountOfTooManyOptions = hasTooManyOptions ? nbrOfColors - maxVariants : 0;
    const visibleOptions = colors.slice(0, maxVariants);

    return (
      <div className={styles.optionContainer}>
        {visibleOptions.map((option, index) => {
          const updateImage = () => {
            !isMobile && debounce(() => setImage && setImage(option));
          };

          const setDefaultImage = () => {
            !isMobile && debounce(() => setImage && setImage(defaultImage));
          };

          return (
            <NotifyLink
              key={`${index}-${option.id}`}
              url={url}
              ticket={ticket}
              className={styles.option}
            >
              <Image
                onMouseOver={updateImage}
                onMouseOut={setDefaultImage}
                wrapperStyle={{ width: '100%' }}
                className={styles.image}
                paddingBottom
                {...option}
                height={IMAGE_SIZE}
                width={IMAGE_SIZE}
                alt={option.alt}
              />
            </NotifyLink>
          );
        })}
        {!!amountOfTooManyOptions && (
          <NotifyLink url={url} ticket={ticket} className={styles.tooManyOptions}>
            <p className={styles.tooManyOptionsText}>+{amountOfTooManyOptions}</p>
          </NotifyLink>
        )}
      </div>
    );
  }
);

const VariantOptions = connect(state => ({
  isMobile: isMobile(state)
}))(_VariantOptions);

interface OwnProps extends ProductlistViewProduct {
  isListView?: boolean;
  isTopTested: boolean;
  isSponsored?: boolean;
  isCategoryBold?: boolean;
  setImage?: (image: BhImage) => void;
  image?: BhImage;
  isSponsoredByListView?: boolean;
  hideVariants?: boolean;
  colours: string[];
  page?: string;
  maxVariants?: number;
  picture: {
    className?: string;
    height: number;
    width: number;
  };
  inList?: boolean;
  children?: React.ReactNode;
  customSizes: { [key: string]: SrcSet };
  isLazy?: boolean;
  isDisabled?: boolean;
  usps?: string[];
  gtmProduct?: GtmProduct;
}

const ProductCardImages = ({
  isListView,
  productImage,
  url,
  picture,
  ticket,
  isSponsored,
  setImage,
  image,
  colorOptions,
  hideVariants,
  maxVariants,
  customSizes,
  productId,
  isLazy,
  isDisabled,
  gtmProduct
}: Pick<
  OwnProps,
  | 'isListView'
  | 'productImage'
  | 'url'
  | 'picture'
  | 'ticket'
  | 'isSponsored'
  | 'colorOptions'
  | 'hideVariants'
  | 'maxVariants'
  | 'customSizes'
  | 'setImage'
  | 'image'
  | 'productId'
  | 'isLazy'
  | 'isDisabled'
  | 'gtmProduct'
>) => {
  return (
    <>
      <HeaderImage
        isLazy={isLazy}
        productId={productId}
        image={image}
        url={url}
        picture={picture}
        ticket={ticket}
        className={cn({
          [styles.switchHeaderImage]: isListView,
          [styles.headerStyle]: !isListView
        })}
        isSponsored={isSponsored}
        isListView={isListView}
        customSizes={customSizes}
        isDisabled={isDisabled}
        gtmProduct={gtmProduct}
      />
      <VariantOptions
        productId={productId}
        isListView={isListView}
        maxVariants={maxVariants}
        setImage={setImage}
        hideVariants={hideVariants || isListView}
        defaultImage={productImage}
        colorOptions={colorOptions}
        image={productImage}
        url={url}
        picture={picture}
        ticket={ticket}
      />
    </>
  );
};

export const ProductCard = forwardRef(
  (
    {
      productImage,
      url,
      title,
      titleSplitPos,
      price,
      isCampaign,
      isPartialCampaign,
      isLowPrice,
      isLoweredPrice,
      splashTextMiddle,
      splashTextEnd,
      splashTextTop,
      isTopTested,
      productId,
      efficiencyDeclarationUrl,
      efficiencyArrow,
      reviewAvgScore,
      isNewProduct,
      meta,
      isCategoryBold,
      isListView,
      isSponsored,
      isSponsoredByListView,
      picture,
      ticket,
      colorOptions,
      hideVariants,
      isPartPrice,
      children,
      page,
      partPriceUnit,
      maxVariants,
      customSizes,
      isLazy,
      isDisabled,
      usps,
      tacticalFlagMarking,
      tacticalFlagColor,
      brandName,
      mainWebCatalog
    }: OwnProps & Pick<BetterPriceProps, 'page'>,
    ref?: Ref<HTMLDivElement>
  ) => {
    const [image, setImage] = useState(productImage);
    const isMobile = useSelector(isMobileSelector);

    if (!productId) {
      return NO_ELEMENT;
    }

    const gtmProduct: GtmProduct = createGtmProduct({
      url,
      brandName,
      productId,
      title,
      variantName: '',
      presentationPrice: price.presentationPrice,
      item_web_catalog_id: mainWebCatalog
    });

    const hasOptions = colorOptions?.nbrOfColors > 1;

    const showProductProperties = page !== 'topsellers' && !isSponsoredByListView;

    const ProductInformationCard = (
      <div>
        <div>
          <Review
            score={reviewAvgScore}
            isTopTested={isTopTested}
            isNewProduct={isNewProduct}
            tacticalFlagMarking={tacticalFlagMarking}
            tacticalFlagColor={tacticalFlagColor}
            isListView={isListView}
          />
          <Title {...{ ticket, isCategoryBold, title, titleSplitPos, url }} />
          {showProductProperties && <ProductProperties props={usps} />}
          <CardPrice
            page={page ? page : isListView ? 'switchProductlist' : 'productlist'}
            splashTextMiddle={splashTextMiddle}
            splashTextEnd={splashTextEnd}
            splashTextTop={splashTextTop}
            isCampaign={isCampaign}
            isPartialCampaign={isPartialCampaign}
            isLowPrice={isLowPrice}
            isLoweredPrice={isLoweredPrice}
            price={price}
            salesUnit={partPriceUnit}
            inCard
            partPrice={isPartPrice ? price.presentationPrice : 0}
            partPriceUnit={partPriceUnit}
          />
        </div>
        <EnergyData
          appearance="smallMedium"
          productId={productId}
          efficiencyArrow={efficiencyArrow}
          efficiencyDeclarationUrl={efficiencyDeclarationUrl}
          classNames={styles.energy}
        />
        <Delivery meta={meta} isListView={isListView} />
      </div>
    );

    const images = (
      <ProductCardImages
        maxVariants={maxVariants}
        hideVariants={hideVariants}
        colorOptions={colorOptions}
        picture={picture}
        productImage={productImage}
        ticket={ticket}
        url={url}
        isListView={isListView}
        isSponsored={isSponsored}
        customSizes={customSizes}
        setImage={setImage}
        image={image}
        productId={productId}
        isLazy={isLazy}
        isDisabled={isDisabled}
        gtmProduct={gtmProduct}
      />
    );

    return (
      <div
        className={cn(styles.productCardContainer, {
          [styles.cellStyle]: isListView,
          [styles.sponsoredByListView]: isSponsoredByListView
        })}
        {...getQaDataAttr(`productcard-${productId}`)}
      >
        {images}
        <div
          ref={ref}
          className={cn({
            [styles.switchDetailsContainer]: isListView,
            [styles.optionSpacing]: hasOptions
          })}
        >
          {isMobile ? (
            <NotifyLink
              url={url}
              ticket={ticket}
              style={{ height: '100%' }}
              isDisabled={isDisabled}
            >
              {ProductInformationCard}
            </NotifyLink>
          ) : (
            ProductInformationCard
          )}
        </div>
        <div>{children}</div>
      </div>
    );
  }
);
